import { useEffect, useState } from 'react';

import { getProjectDetails } from '@/api/project-details/api';
import type { StatusUpdate } from '@/api/project-details/mocks/projectHome.types';
import { createLogger } from '@/utils/logging';

const logger = createLogger('hooks', 'useWeeklyUpdateData');

interface UseWeeklyUpdateDataReturn {
  data: StatusUpdate | null;
  loading: boolean;
  error: Error | null;
}

/**
 * Fetch a specific week's weekly update data by week date.
 * Fetches all weekly updates from the project and filters for the specific week.
 */
export function useWeeklyUpdateData(
  accessToken: string | null | undefined,
  clientId: string,
  projectId: string,
  week: string,
): UseWeeklyUpdateDataReturn {
  const [data, setData] = useState<StatusUpdate | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!accessToken || !week) {
      setData(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchWeeklyUpdate = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all project details including updates
        const details = await getProjectDetails(
          accessToken,
          clientId,
          projectId,
          'metadata,team,health,trends,audit,updates',
        );

        if (cancelled) {
          return;
        }

        // Find the specific week's update
        // statusUpdates is the array of weekly updates in the API response
        const weekUpdate = (details.statusUpdates || []).find(
          (update: StatusUpdate) => update.weekOf === week,
        );

        setData(weekUpdate || null);
      } catch (err) {
        if (!cancelled) {
          const error = err instanceof Error ? err : new Error(String(err));
          logger.error(`Failed to fetch weekly update for week ${week}:`, error);
          setError(error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void fetchWeeklyUpdate();

    return () => {
      cancelled = true;
    };
  }, [accessToken, clientId, projectId, week]);

  return { data, loading, error };
}
